import { z } from "zod";
import { baseLeaderboardEntrySchema } from "../leaderboard/leaderboard.js";

export const VOICE_GUESSER_LEADERBOARD_SCOPE = {
	GLOBAL: "global",
	WEEKLY: "weekly",
	MONTHLY: "monthly",
	SESSION: "session",
} as const;

export const VOICE_GUESSER_LEADERBOARD_SCOPE_VALUES = [
	VOICE_GUESSER_LEADERBOARD_SCOPE.GLOBAL,
	VOICE_GUESSER_LEADERBOARD_SCOPE.WEEKLY,
	VOICE_GUESSER_LEADERBOARD_SCOPE.MONTHLY,
	VOICE_GUESSER_LEADERBOARD_SCOPE.SESSION,
] as const;

export const VOICE_GUESSER_PERSISTENT_LEADERBOARD_SCOPE_VALUES = [
	VOICE_GUESSER_LEADERBOARD_SCOPE.GLOBAL,
	VOICE_GUESSER_LEADERBOARD_SCOPE.WEEKLY,
	VOICE_GUESSER_LEADERBOARD_SCOPE.MONTHLY,
] as const;

export const voiceGuesserLeaderboardScopeSchema = z.enum(VOICE_GUESSER_LEADERBOARD_SCOPE_VALUES);
export type VoiceGuesserLeaderboardScope = z.infer<typeof voiceGuesserLeaderboardScopeSchema>;

export const voiceGuesserPersistentLeaderboardScopeSchema = z.enum(VOICE_GUESSER_PERSISTENT_LEADERBOARD_SCOPE_VALUES);
export type VoiceGuesserPersistentLeaderboardScope = z.infer<typeof voiceGuesserPersistentLeaderboardScopeSchema>;

export const voiceGuesserLeaderboardSeasonSchema = z.object({
	scope: z.enum([VOICE_GUESSER_LEADERBOARD_SCOPE.WEEKLY, VOICE_GUESSER_LEADERBOARD_SCOPE.MONTHLY]),
	key: z.string().min(1),
	starts_at: z.number().int().positive(),
	ends_at: z.number().int().positive(),
});

export type VoiceGuesserLeaderboardSeason = z.infer<typeof voiceGuesserLeaderboardSeasonSchema>;

export const voiceGuesserLeaderboardEntrySchema = baseLeaderboardEntrySchema.extend({
	total_score: z.number().int().nonnegative(),
	season_score: z.number().int().nonnegative(),
	session_score: z.number().int().nonnegative(),
	correct_answers: z.number().int().nonnegative(),
	average_response_time: z.number().nonnegative().nullable(),
	best_streak: z.number().int().nonnegative(),
	current_streak: z.number().int().nonnegative(),
	score_reached_at: z.number().int().positive().nullable(),
});

export type VoiceGuesserLeaderboardEntry = z.infer<typeof voiceGuesserLeaderboardEntrySchema>;

export const voiceGuesserLeaderboardResponseSchema = z.object({
	scope: voiceGuesserPersistentLeaderboardScopeSchema,
	season: voiceGuesserLeaderboardSeasonSchema.nullable(),
	entries: z.array(voiceGuesserLeaderboardEntrySchema),
	current_user_entry: voiceGuesserLeaderboardEntrySchema.nullable(),
	generated_at: z.number().int().positive(),
});

export type VoiceGuesserLeaderboardResponse = z.infer<typeof voiceGuesserLeaderboardResponseSchema>;

export const voiceGuesserLeaderboardQuerySchema = z.object({
	scope: voiceGuesserPersistentLeaderboardScopeSchema,
	limit: z.coerce.number().int().min(1).max(100).default(25),
});

export type VoiceGuesserLeaderboardQuery = z.infer<typeof voiceGuesserLeaderboardQuerySchema>;

const normalizeAverageResponseTime = (value: number | null): number => value ?? Number.POSITIVE_INFINITY;
const normalizeTimestamp = (value: number | null): number => value ?? Number.POSITIVE_INFINITY;

export const compareVoiceGuesserLeaderboardEntries = (
	left: Pick<VoiceGuesserLeaderboardEntry, "score" | "average_response_time" | "correct_answers" | "score_reached_at" | "user_id">,
	right: Pick<VoiceGuesserLeaderboardEntry, "score" | "average_response_time" | "correct_answers" | "score_reached_at" | "user_id">,
): number => {
	if (left.score !== right.score) return right.score - left.score;
	const averageDiff = normalizeAverageResponseTime(left.average_response_time) - normalizeAverageResponseTime(right.average_response_time);
	if (averageDiff !== 0) return averageDiff;
	if (left.correct_answers !== right.correct_answers) return right.correct_answers - left.correct_answers;
	const reachedDiff = normalizeTimestamp(left.score_reached_at) - normalizeTimestamp(right.score_reached_at);
	if (reachedDiff !== 0) return reachedDiff;
	return left.user_id.localeCompare(right.user_id);
};

export const rankVoiceGuesserLeaderboardEntries = (
	entries: readonly Omit<VoiceGuesserLeaderboardEntry, "rank">[],
): VoiceGuesserLeaderboardEntry[] =>
	entries
		.map((entry) => ({ ...entry }))
		.sort(compareVoiceGuesserLeaderboardEntries)
		.map((entry, index) => ({ ...entry, rank: index + 1 }));
